import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Flex, Row, Table, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { hasBillingAccess, hasCapability } from '../access';
import { formatDuration, formatINR } from '../format';
import { useZenBootstrap } from '../hooks';
import { departmentLoad, departmentTimings, dashboardKpis, needsAttention, type AttentionItem } from '../insight';
import { orderStatus } from '../status';
import type { WorkOrder } from '../types';
import { DueText, EmptyState, OrderNumber, PageSkeleton, StatusPill } from '../ui';

const { Text } = Typography;

function greeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function lede(overdue: number, dueToday: number): string {
  const orders = (count: number) => `${count} ${count === 1 ? 'order is' : 'orders are'}`;
  if (overdue && dueToday) return `${orders(overdue)} late and ${dueToday} due today.`;
  if (overdue) return `${orders(overdue)} late. Nothing else is due today.`;
  if (dueToday) return `${orders(dueToday)} due today. Nothing is late.`;
  return 'Nothing is late or due today.';
}

function KpiTile({ label, value, sub, to, signal = false }: { label: string; value: string | number; sub?: string; to: string; signal?: boolean }) {
  return (
    <Link to={to} className={`ua-kpi${signal ? ' ua-kpi--signal' : ''}`}>
      <span className="ua-overline">{label}</span>
      <span className="ua-kpi-value">{value}</span>
      {sub && <span className="ua-kpi-sub">{sub}</span>}
    </Link>
  );
}

function AttentionAction({ item, billing }: { item: AttentionItem; billing: boolean }) {
  const to = `/work-orders/${item.order.id}`;
  if (item.reason === 'invoice' && billing) return <Link to={to}><Button size="small" type="primary">Record invoice</Button></Link>;
  return <Link to={to}><Button size="small">Open</Button></Link>;
}

export function DashboardPage() {
  const { data, isLoading } = useZenBootstrap();
  if (isLoading || !data) return <PageSkeleton />;
  const user = data.currentUser;
  const billing = hasBillingAccess(user);
  const now = new Date();
  const orders = data.workOrders;
  const kpis = dashboardKpis(orders, now);
  const attention = needsAttention(orders, now).slice(0, 8);
  const load = departmentLoad(data.tasks, data.departments, orders);
  const maxLoad = Math.max(1, ...load.map((row) => row.pending + row.started));
  const timings = departmentTimings(data.tasks, data.departments, now);
  const toInvoice = orders.filter((order) => !order.deletedAt && order.status === 'active' && !order.accountsApproved);
  const balances = new Map<string, { clientId: string; name: string; balance: number }>();
  for (const order of orders) {
    if (order.customerBalance !== undefined && order.customerBalance > 0) {
      balances.set(order.clientId, { clientId: order.clientId, name: order.customerName, balance: order.customerBalance });
    }
  }
  const toCollect = [...balances.values()].sort((left, right) => right.balance - left.balance).slice(0, 8);

  return (
    <Flex vertical gap={20}>
      <Flex justify="space-between" align="flex-end" gap={12} wrap>
        <div>
          <div className="ua-overline">{greeting(now)}, {user.name.split(' ')[0]}</div>
          <div className="ua-dashboard-lede">
            {lede(kpis.overdue, kpis.dueToday)}
          </div>
        </div>
        {hasCapability(user, data.departments, 'create-order') && (
          <Link to="/work-orders/new"><Button type="primary" icon={<PlusOutlined />}>New work order</Button></Link>
        )}
      </Flex>

      <div className="ua-kpi-grid">
        <KpiTile label="Due today" value={kpis.dueToday} sub={kpis.dueTodayNotStarted ? `${kpis.dueTodayNotStarted} not started` : 'All started'} to="/work-orders?view=due-week" />
        <KpiTile label="Overdue" value={kpis.overdue} sub={kpis.overdue ? `Oldest ${kpis.oldestOverdueDays} ${kpis.oldestOverdueDays === 1 ? 'day' : 'days'}` : 'Nothing late'} to="/work-orders?view=overdue" signal={kpis.overdue > 0} />
        <KpiTile label="In progress" value={kpis.inProgress} sub={`${toInvoice.length} awaiting invoice`} to="/work-orders" />
        {billing && (
          <KpiTile label="To collect" value={formatINR(kpis.toCollect)} sub={`Across ${kpis.toCollectCustomers} ${kpis.toCollectCustomers === 1 ? 'customer' : 'customers'}`} to="/accounts" />
        )}
      </div>

      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} xl={15}>
          <Card title={<>Needs attention <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>· sorted by risk</Text></>} styles={{ body: { padding: attention.length ? 0 : undefined } }}>
            {attention.length ? (
              <div className="ua-attention" role="list">
                {attention.map((item) => (
                  <div key={item.order.id} className={`ua-attention-row ua-attention-row--${item.reason}`} role="listitem">
                    <OrderNumber>{item.order.orderNumber}</OrderNumber>
                    <span className="ua-attention-client">{item.order.customerName}</span>
                    <StatusPill status={orderStatus(item.order)} />
                    <span className={`ua-attention-detail${item.reason === 'late' ? ' ua-due ua-due--late' : ''}`}>
                      {item.reason === 'late' && <span className="ua-due-dot" aria-hidden="true" />}
                      {item.detail}
                    </span>
                    <AttentionAction item={item} billing={billing} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="Nothing needs attention" description="Late orders, slow invoices and work left started for over a day show up here." />
            )}
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card title="Department load">
            {load.length ? (
              <ol className="ua-load">
                {load.map((row) => (
                  <li key={row.department.id}>
                    <Flex justify="space-between" gap={8}>
                      <span>{row.department.name}</span>
                      <span className="ua-load-count">{row.pending} waiting · {row.started} started</span>
                    </Flex>
                    <span className="ua-load-bar" aria-hidden="true">
                      <i className="ua-load-started" style={{ width: `${(row.started / maxLoad) * 100}%` }} />
                      <i className="ua-load-pending" style={{ width: `${(row.pending / maxLoad) * 100}%` }} />
                    </span>
                  </li>
                ))}
              </ol>
            ) : <EmptyState compact title="No queue departments yet" />}
          </Card>
        </Col>
      </Row>

      {billing && (
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Card title={<>To invoice <span className="ua-count">{toInvoice.length}</span></>} extra={<Link to="/accounts">All</Link>} styles={{ body: { padding: toInvoice.length ? 0 : undefined } }}>
              {toInvoice.length ? (
                <Table<WorkOrder>
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={toInvoice.slice(0, 8)}
                  columns={[
                    { title: 'Order', key: 'order', render: (_, order) => <Link to={`/work-orders/${order.id}`}><OrderNumber>{order.orderNumber}</OrderNumber></Link> },
                    { title: 'Customer', dataIndex: 'customerName', ellipsis: true },
                    { title: 'Due', key: 'due', render: (_, order) => <DueText date={order.deliveryDate} /> },
                  ]}
                />
              ) : <EmptyState compact title="Every order has an invoice" />}
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card title="To collect" extra={<Link to="/accounts">Ledger</Link>} styles={{ body: { padding: toCollect.length ? 0 : undefined } }}>
              {toCollect.length ? (
                <Table
                  rowKey="clientId"
                  size="small"
                  pagination={false}
                  dataSource={toCollect}
                  columns={[
                    { title: 'Customer', dataIndex: 'name', render: (name: string, row) => <Link to={`/accounts/${row.clientId}`}>{name}</Link> },
                    { title: 'Balance due', dataIndex: 'balance', align: 'right', render: (value: number) => <span className="ua-text-warning">{formatINR(value)}</span> },
                  ]}
                />
              ) : <EmptyState compact title="Nothing to collect" description="Customers with an unpaid balance appear here." />}
            </Card>
          </Col>
        </Row>
      )}

      {user.isAdmin && (
        <Card title={<>Time per department <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>· last 30 days</Text></>} styles={{ body: { padding: 0 } }}>
          <Table
            className="zen-responsive-table"
            rowKey={(row) => row.department.id}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={timings}
            columns={[
              { title: 'Department', key: 'department', render: (_, row) => row.department.name },
              { title: 'Jobs done', dataIndex: 'completed', align: 'right' },
              { title: 'Average wait', key: 'wait', align: 'right', render: (_, row) => row.averageWaitMs === null ? '—' : formatDuration(row.averageWaitMs) },
              { title: 'Average work', key: 'work', align: 'right', render: (_, row) => row.averageWorkMs === null ? '—' : formatDuration(row.averageWorkMs) },
            ]}
          />
        </Card>
      )}
    </Flex>
  );
}
